import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LineChart, Line, ComposedChart, Legend, PieChart, Pie } from "recharts";
import { TrendingUp, Package, Coins, Timer, Truck, MapPin } from "lucide-react";
import { KpiCard } from "@/components/KpiCard";
import { ChartCard } from "@/components/ChartCard";
import AiInsightsPanel from "@/components/AiInsightsPanel";
import DrillDownModal from "@/components/DrillDownModal";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useFilters } from "@/state/FilterContext";
import { formatTRY, formatNumber } from "@/lib/api";
import { CHART_PALETTE, SAP_BLUE } from "@/lib/palette";

const TooltipBox = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-md px-3 py-2 text-xs">
      <div className="font-semibold text-slate-800 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-slate-600">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color || p.fill }} />
          <span>{p.name}:</span>
          <span className="font-semibold text-slate-900 tabular-nums">
            {formatter ? formatter(p.value, p.name) : formatNumber(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function Overview() {
  const { data: kpi } = useDashboardData("/dashboard/kpis");
  const { data: ratio } = useDashboardData("/dashboard/source-location-ratio");
  const { data: trend } = useDashboardData("/dashboard/time-trend", { days: 365 });
  const { data: modes } = useDashboardData("/dashboard/mode-breakdown");
  const { meta } = useFilters();
  const [drill, setDrill] = useState({ open: false, title: "", overrides: {} });

  const locCode = (name) => meta.locations?.find((l) => l.name === name)?.code || name;
  const openLocationDrill = (locationName) =>
    setDrill({ open: true, title: `${locationName} – Sevkiyatlar`, overrides: { location: locCode(locationName) } });
  const openModeDrill = (modeName) =>
    setDrill({ open: true, title: `${modeName} – Sevkiyatlar`, overrides: { mode: modeName } });

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-total-shipments" label="Toplam Sevkiyat" icon={Package}
          value={formatNumber(kpi?.total_shipments ?? 0)} delta={kpi?.delta_shipments_pct} />
        <KpiCard testid="kpi-total-cost" label="Toplam Maliyet" icon={Coins}
          value={formatTRY(kpi?.total_cost ?? 0)} delta={kpi?.delta_cost_pct} accent="#0EA5E9" />
        <KpiCard testid="kpi-avg-cost" label="Ort. Sevkiyat Maliyeti" icon={TrendingUp}
          value={formatTRY(kpi?.avg_cost ?? 0)} accent="#1E293B" />
        <KpiCard testid="kpi-on-time" label="Zamanında Teslimat" icon={Timer}
          value={`%${formatNumber(kpi?.on_time_rate ?? 0)}`} accent="#10B981" />
      </div>

      {/* AI Insights */}
      <AiInsightsPanel />

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard
          testid="chart-source-ratio"
          className="lg:col-span-2"
          title="Kaynak Lokasyon Taşıma Oranları"
          subtitle="Lokasyon bazında sevkiyat hacmi ve payı"
        >
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={ratio || []} layout="vertical" margin={{ left: 20, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis type="category" dataKey="location" tick={{ fontSize: 12, fill: "#334155" }} width={140} />
              <Tooltip content={<TooltipBox formatter={(v, n) => n === "Hacim" ? formatNumber(v) : formatTRY(v)} />} />
              <Bar
                dataKey="volume"
                name="Hacim"
                radius={[0, 4, 4, 0]}
                cursor="pointer"
                onClick={(d) => d?.location && openLocationDrill(d.location)}
              >
                {(ratio || []).map((_, i) => (
                  <Cell key={i} fill={i === 0 ? SAP_BLUE : CHART_PALETTE[(i % CHART_PALETTE.length)]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testid="chart-mode-breakdown" title="Taşıma Modu Dağılımı" subtitle="Sevkiyat sayısına göre">
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={modes || []} dataKey="volume" nameKey="mode" innerRadius={60} outerRadius={100} paddingAngle={2}
                cursor="pointer"
                onClick={(d) => d?.mode && openModeDrill(d.mode)}>
                {(modes || []).map((_, i) => (
                  <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip content={<TooltipBox />} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Trend */}
      <ChartCard
        testid="chart-trend"
        title="Aylık Maliyet ve Hacim Trendi"
        subtitle="Son 12 ay için sevkiyat sayısı ve toplam maliyet"
      >
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={trend || []} margin={{ left: 8, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#64748B" }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#64748B" }} />
            <Tooltip content={<TooltipBox formatter={(v, n) => n === "Maliyet" ? formatTRY(v) : formatNumber(v)} />} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="volume" name="Hacim" fill="#BAE6FD" radius={[4, 4, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="cost" name="Maliyet" stroke={SAP_BLUE} strokeWidth={2.5} dot={{ r: 3, fill: SAP_BLUE }} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Top routes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard testid="chart-top-locations" title="En Aktif Kaynak Lokasyonlar" subtitle="Hacim payı (%)">
          <div className="space-y-3">
            {(ratio || []).slice(0, 6).map((r, i) => (
              <button
                key={r.location}
                data-testid={`top-location-${i}`}
                onClick={() => openLocationDrill(r.location)}
                className="w-full text-left group"
              >
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <div className="flex items-center gap-2 text-slate-700 group-hover:text-[#0054E3] transition-colors">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-medium">{r.location}</span>
                  </div>
                  <span className="text-slate-900 font-semibold tabular-nums">%{r.ratio}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${r.ratio}%`, background: i === 0 ? SAP_BLUE : "#00B0FF" }}
                  />
                </div>
              </button>
            ))}
          </div>
        </ChartCard>

        <ChartCard testid="chart-secondary-kpi" title="Ek Performans Göstergeleri" subtitle="Filtrelere göre">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">Toplam Ağırlık</div>
              <div className="font-display text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                {formatNumber(kpi?.total_tons ?? 0)} <span className="text-sm text-slate-500 font-medium">ton</span>
              </div>
            </div>
            <div className="rounded-md border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">Maliyet / Ton</div>
              <div className="font-display text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                {formatTRY(kpi?.cost_per_ton ?? 0)}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 p-4 col-span-2 bg-[#F8FAFC]">
              <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold flex items-center gap-2">
                <Truck className="w-3.5 h-3.5" /> Hızlı Eylem
              </div>
              <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                Filtreleri değiştirerek farklı lokasyon/nakliyeci/dönem kombinasyonlarını anında karşılaştırın.
                AI içgörüler her filtre değişiminde otomatik güncellenir.
              </p>
            </div>
          </div>
        </ChartCard>
      </div>

      <DrillDownModal
        open={drill.open}
        onOpenChange={(o) => setDrill((d) => ({ ...d, open: o }))}
        title={drill.title}
        overrides={drill.overrides}
      />
    </div>
  );
}
